import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { CartItemsComponent } from '@theme/component-cart-items';
import { ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

/**
 * A cart line's size (or any option with more than one value) changed in
 * place (snippets/birrot-cart-line-options.liquid), in the drawer and on
 * the cart page: the chosen variant is added at the line's quantity, then
 * the line is removed, and the section comes back with the removal.
 *
 * Added, not set: /cart/update.js sets a variant's quantity, so a size
 * already in the cart on another line lost that line's quantity. /cart/add.js
 * merges into such a line (same variant, no properties) and refuses what
 * the stock cannot cover, before anything has changed.
 *
 * The swap waits for the stepper: a quantity change still in its debounce
 * or in flight (component-cart-items.js) is let land first, and the line's
 * quantity is read after it, so the two requests never race.
 *
 * The removal goes out as Horizon's own cart event, so what listens to a
 * quantity change hears this one too: the cart items component morphs the
 * section from `detail.sections`, the header keeps its count. A refused
 * swap (sold out since the cart rendered) puts the picker back and shows
 * the store's reason in the line's error row, as a refused quantity does;
 * a cart that does not answer in JSON (the dev proxy's 502 page) shows
 * the snippet's own text (`data-birrot-error`). The event's promise is
 * made only once the add has gone through, right before the event that
 * carries it: rejected earlier, nothing has subscribed to it and the
 * browser reports an unhandled rejection.
 */

/** component-cart-items.js's debounce on a stepper change, and a margin. */
const QUANTITY_DEBOUNCE = 300 + 50;

let lastQuantityChange = -Infinity;
document.addEventListener(ThemeEvents.quantitySelectorUpdate, () => {
  lastQuantityChange = performance.now();
});

/** Resolves once no stepper change is waiting on its debounce or in flight. */
async function quantityChangesSettled() {
  for (;;) {
    const wait = lastQuantityChange + QUANTITY_DEBOUNCE - performance.now();
    if (wait > 0) {
      await new Promise((resolve) => setTimeout(resolve, wait));
      continue;
    }
    const pending = CartItemsComponent.pendingQuantityUpdates;
    if (!pending.length) return;
    await Promise.allSettled(pending);
  }
}

/**
 * The cart's answer, or the reason it refused. A body that is not JSON (a
 * proxy's error page, whatever its status) is a refusal with `fallback` as
 * its text, not a parse error's, and never a cart.
 * @param {Response} response
 * @param {string} fallback
 */
async function cartJson(response, fallback) {
  const json = await response.json().catch(() => null);
  if (!json || !response.ok || json.status || json.errors) {
    throw new Error(json?.description || json?.errors || json?.message || fallback);
  }
  return json;
}

document.addEventListener('change', async (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('[data-birrot-line-option]')) return;

  const { lineKey = '', variantId = '' } = select.dataset;
  const next = select.value;
  if (!next || next === variantId) return;

  const component = select.closest('cart-items-component');
  const sectionId = component instanceof HTMLElement ? component.dataset.sectionId : undefined;
  const mode = component instanceof CartItemsComponent && component.isDrawer ? 'hydration' : 'full';
  const fallback = select.closest('[data-birrot-error]')?.getAttribute('data-birrot-error') || 'Variant change refused';
  const shown = select.parentElement?.querySelector('[data-birrot-option-value]');
  const before = shown?.textContent ?? '';
  if (shown) shown.textContent = select.selectedOptions[0]?.textContent ?? before;
  component?.classList.add('cart-items-disabled');

  await quantityChangesSettled();

  // The line as it is now: a stepper change may have morphed it (and set
  // the select back to the line's variant), or removed it.
  const line = component?.querySelector(`[data-birrot-line-option][data-line-key="${CSS.escape(lineKey)}"]`);
  if (!(line instanceof HTMLSelectElement)) {
    component?.classList.remove('cart-items-disabled');
    return;
  }
  const quantity = Number(line.dataset.quantity) || 1;
  component?.classList.add('cart-items-disabled');

  /** @type {ReturnType<typeof CartLinesUpdateEvent.createPromise> | undefined} */
  let deferred;
  try {
    await cartJson(
      await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', { body: JSON.stringify({ items: [{ id: Number(next), quantity }] }) })
      ),
      fallback
    );

    deferred = CartLinesUpdateEvent.createPromise();
    line.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [{ id: lineKey, quantity: 0 }],
        promise: deferred.promise,
      })
    );

    const cart = await cartJson(
      await fetch(
        Theme.routes.cart_change_url,
        fetchConfig('json', {
          body: JSON.stringify({
            id: lineKey,
            quantity: 0,
            ...(sectionId && { sections: sectionId, sections_url: window.location.pathname }),
          }),
        })
      ),
      fallback
    );

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: cart.sections ?? undefined,
        items: cart.items,
        itemCount: cart.item_count,
        source: 'birrot-cart-line-options',
        didError: false,
      },
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    deferred?.reject(error);
    line.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));

    if (deferred && sectionId) {
      // Added but not removed: the cart holds both lines, so show it as it is.
      sectionRenderer.renderSection(sectionId, { cache: false, mode }).catch(() => {});
    } else {
      const current = component?.querySelector(`[data-birrot-line-option][data-line-key="${CSS.escape(lineKey)}"]`);
      const target = current instanceof HTMLSelectElement ? current : select;
      target.value = target.dataset.variantId ?? variantId;
      const label = target.parentElement?.querySelector('[data-birrot-option-value]');
      if (label) label.textContent = target.selectedOptions[0]?.textContent ?? before;

      const row = target.closest('tr');
      const errorRow = row?.querySelector('.cart-items__error');
      const errorText = row?.querySelector('.cart-item__error-text');
      if (errorRow && errorText) {
        errorText.textContent = message;
        errorRow.classList.remove('hidden');
      }
    }
  } finally {
    component?.classList.remove('cart-items-disabled');
  }
});
